// Gallery.jsx
import "./style.scss";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@mui/material";
import ImageGallery from "react-image-gallery";
import { getImagesForLocation } from "../../assets/gallery/galleryImages";
import { getLocationImages, preloadImage } from "../../utils/imageLoader";
import { useSnackbar } from "../../hooks/useSnackbar";
import {
  LOCATIONS,
  getUserLocation,
  findNearestLocation,
} from "../../config/locations";

/** Portal overlay that mounts into <body> so it sits above everything. */
const PortalOverlay = ({ children }) => {
  return createPortal(
    <div className="gallery-portal-overlay">{children}</div>,
    document.body
  );
};

const Gallery = () => {
  const [selectedLocation, setSelectedLocation] = useState("");
  const [autoDetectedLocation, setAutoDetectedLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState("prompt"); // 'granted' | 'denied' | 'prompt' | 'unsupported'
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [images, setImages] = useState([]);
  const { showError, showSuccess } = useSnackbar();

  // Get images based on selected or auto-detected location
  const getCurrentImages = (location) => {
    const locationToUse = location || autoDetectedLocation?.location;

    let locationImages = getImagesForLocation(locationToUse);
    if (!locationImages || locationImages.length === 0) {
      locationImages = getLocationImages(locationToUse);
    }

    return (locationImages || [])
      .filter(Boolean)
      .map((img) => ({ original: img, thumbnail: img }))
      .sort(() => Math.random() - 0.5);
  };

  useEffect(() => {
    const savedLocation = localStorage.getItem("selectedLocation");
    if (savedLocation) {
      setSelectedLocation(savedLocation);
      setImages(getCurrentImages(savedLocation));
    } else {
      setImages(getCurrentImages(null));
    }
    detectUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload first gallery image for faster display
  useEffect(() => {
    const firstSrc = images[0]?.original;
    if (firstSrc) return preloadImage(firstSrc);
  }, [images]);

  const detectUserLocation = async () => {
    setIsLoadingLocation(true);
    try {
      if (!navigator.geolocation) {
        setLocationPermission("unsupported");
        return;
      }
      const coords = await getUserLocation();
      setLocationPermission("granted");

      const nearest = findNearestLocation(coords.lat, coords.lng);
      setAutoDetectedLocation(nearest);

      if (nearest.distance <= 50 && !selectedLocation) {
        setSelectedLocation(nearest.location);
        const locationImages = getCurrentImages(nearest.location);
        setImages(locationImages);
        localStorage.setItem("selectedLocation", nearest.location);
        showSuccess(
          `Auto-detected: ${
            LOCATIONS[nearest.location].displayName
          } (${Math.round(nearest.distance)}km away)`
        );
      }
    } catch (error) {
      setLocationPermission("denied");
      if (error?.code === 1) {
        showError("Location access denied. Please select location manually.");
      } else if (error?.code === 2) {
        showError("Location unavailable. Please select location manually.");
      } else if (error?.code === 3) {
        showError(
          "Location request timed out. Please select location manually."
        );
      } else {
        showError("Could not detect location. Please select manually.");
      }
    } finally {
      // finally always runs even if we returned from inside try
      setIsLoadingLocation(false);
    }
  };

  const handleLocationChange = (e) => {
    const newLocation = e.target.value;
    setSelectedLocation(newLocation);

    if (newLocation) {
      const locationImages = getCurrentImages(newLocation);
      setImages(locationImages);
      localStorage.setItem("selectedLocation", newLocation);
    } else {
      setImages(getCurrentImages(null));
      localStorage.removeItem("selectedLocation");
    }
  };

  const renderLocationInfo = () => {
    if (locationPermission === "granted" && autoDetectedLocation) {
      return (
        <div className="location-info">
          📍 Near {LOCATIONS[autoDetectedLocation.location].displayName} (
          {Math.round(autoDetectedLocation.distance)}km away)
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <div className="gallery-wrapper">
        <ImageGallery
          items={images}
          showFullscreenButton={false}
          showPlayButton={false}
          showThumbnails={false}
          lazyLoad={true}
          autoPlay={true}
          additionalClass="gallery-image"
        />
      </div>

      {/* Controls are now rendered in a PORTAL so the select is always clickable */}
      <PortalOverlay>
        {/* {renderLocationInfo()} */}
        <div className="gallery-controls">
          <select
            value={selectedLocation}
            onChange={handleLocationChange}
            className={`gallery-select ${
              !selectedLocation ? "placeholder" : "selected"
            }`}
            disabled={isLoadingLocation}
          >
            <option value="">
              {isLoadingLocation ? "Detecting location..." : "Select Location"}
            </option>
            <option value="srinagar">Sonwar Bagh, Srinagar (J&K)</option>
            {/* <option value="baramulla">SSM College, Pattan (J&K)</option> */}
            <option value="jammu">KC Sports Club, Paloura, Akhnoor Road, Jammu (J&K)</option>
          </select>

          <Button
            onClick={detectUserLocation}
            disabled={isLoadingLocation}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              color: "white",
              textTransform: "unset",
              minWidth: "40px",
              padding: "8px",
              marginLeft: "10px",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
              "&:disabled": {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                color: "rgba(255, 255, 255, 0.5)",
              },
            }}
            style={{ height: "50px" }}
          >
            {isLoadingLocation ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              <i className="fa-solid fa-location-crosshairs"></i>
            )}
          </Button>
        </div>
      </PortalOverlay>

      <div className="footer" />
    </>
  );
};

export default Gallery;
